import type { ActiveCase, NewCase } from '#entities';
import type { ParsedDonorReference } from './parseDonorReference';

type CaseLike = Pick<ActiveCase | NewCase, 'unosDonorId' | 'organ'>;

const matchesParsed = (
  caseItem: CaseLike,
  parsed: ParsedDonorReference,
): boolean =>
  // Guard against null: secondary/organ-only parsed entries can have a null
  // donorId, and a case's unosDonorId is never null — never let null match null.
  parsed.donorId !== null &&
  caseItem.unosDonorId === parsed.donorId &&
  caseItem.organ.name === parsed.organ;

/**
 * Resolves the case an aircraft's trip refers to, preferring the PRIMARY
 * (first) organ in the parsed External Reference.
 *
 * Multi-organ references (e.g. "ABCD123HT/LI") parse into several organs in
 * reference order. The aircraft color/label is derived from the resolved
 * case, so it must follow the FIRST case in the reference — not whichever
 * matching case happens to come first in the `cases` array (TSMED-1396).
 *
 * Falls back to the first case matching any organ in the reference, so a
 * multi-organ trip whose primary case isn't loaded still resolves a case.
 */
export const findCaseForReference = <T extends CaseLike>(
  cases: T[],
  allParsed: ParsedDonorReference[],
): T | undefined => {
  const [primary] = allParsed;

  return (
    (primary && cases.find(caseItem => matchesParsed(caseItem, primary))) ||
    cases.find(caseItem => allParsed.some(p => matchesParsed(caseItem, p)))
  );
};
